import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { setTherapist } from '../../api/chat'
import { useChatStore } from '../../stores/chatStore'
import IntroVideoCircle from './IntroVideoCircle'
import { useUserLanguage } from '../../hooks/useUserLanguage'

interface TherapistOption {
  type: string
  name: string
  description: string
  avatar_url: string
  is_current?: boolean
}

interface TherapistSwitcherProps {
  currentType?: string
  onClose: () => void
}

const THERAPISTS: TherapistOption[] = [
  {
    type: 'wendy',
    name: 'Венди',
    description: 'ACT, IFS, Somatic · Тёплый и эмпатичный',
    avatar_url: '/avatars/wendy.png',
  },
  {
    type: 'artem',
    name: 'Артём',
    description: 'REBT, Gestalt · Прямой и провокативный',
    avatar_url: '/avatars/artem.png',
  },
  {
    type: 'una',
    name: 'Уна',
    description: 'EFT, Gottman · Отношения и расставания',
    avatar_url: '/avatars/una.png',
  },
  {
    type: 'finch',
    name: 'Гарольд',
    description: 'MI, психодинамика · Онбординг и супервизия',
    avatar_url: '/avatars/finch.png',
  },
]

export default function TherapistSwitcher({ currentType = 'wendy', onClose }: TherapistSwitcherProps) {
  const [selected, setSelected] = useState(currentType)
  const lang = useUserLanguage()
  const queryClient = useQueryClient()
  const { clearMessages, setTherapist: setStoreTherapist } = useChatStore()

  const mutation = useMutation({
    mutationFn: (therapistType: string) => setTherapist(therapistType),
    onSuccess: (newTherapist) => {
      // Clear old messages immediately to prevent mixed content
      clearMessages()
      // Update store with new therapist
      setStoreTherapist(newTherapist)
      // Reset therapist status to idle (prevents stuck "typing" state)
      useChatStore.getState().setTherapistStatus('idle')
      // Clear react-query cache BEFORE invalidating — otherwise the stale
      // cached historyData re-populates zustand via ChatPage's useEffect
      queryClient.setQueryData(['chatHistory'], null)
      queryClient.invalidateQueries({ queryKey: ['currentTherapist'] })
      queryClient.invalidateQueries({ queryKey: ['chatHistory'] })
      onClose()
    },
  })

  const handleSelect = (type: string) => {
    setSelected(type)
    mutation.mutate(type)
  }

  const therapistColors: Record<string, string> = {
    wendy: 'border-wendy bg-wendy/10',
    artem: 'border-artem bg-artem/10',
    una: 'border-una bg-una/10',
    finch: 'border-finch bg-finch/10',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div data-testid="therapist-switcher" className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md mx-auto pt-6 px-6 pb-8 safe-area-pb animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-neutral-800">
            Выбрать терапевта
          </h2>
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-neutral-400 hover:text-neutral-600 rounded-full"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Therapist Options */}
        <div className="space-y-3">
          {THERAPISTS.map((therapist) => (
            <button
              key={therapist.type}
              data-testid={`therapist-option-${therapist.type}`}
              onClick={() => handleSelect(therapist.type)}
              disabled={mutation.isPending}
              className={clsx(
                'w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all',
                selected === therapist.type
                  ? therapistColors[therapist.type] || 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300 bg-white',
                mutation.isPending && 'opacity-50 cursor-not-allowed'
              )}
            >
              {/* Video circle preview */}
              <IntroVideoCircle
                therapistType={therapist.type}
                lang={lang}
                size="sm"
                autoPlay={false}
                fallbackAvatarUrl={therapist.avatar_url}
              />

              {/* Info */}
              <div className="flex-1 text-left">
                <h3 className="font-medium text-neutral-800">
                  {therapist.name}
                </h3>
                <p className="text-sm text-neutral-500">
                  {therapist.description}
                </p>
              </div>

              {/* Checkmark */}
              {selected === therapist.type && (
                <div className={clsx(
                  'w-6 h-6 rounded-full flex items-center justify-center',
                  therapist.type === 'wendy' && 'bg-wendy',
                  therapist.type === 'artem' && 'bg-artem',
                  therapist.type === 'una' && 'bg-una',
                  therapist.type === 'finch' && 'bg-finch',
                )}>
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Loading indicator */}
        {mutation.isPending && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-2xl">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  )
}
